import { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { organizationService, Organization } from '@services/organizationService';
import { toDisplayError } from '../../utils/apiError';

const updateOrgSchema = z.object({
  name: z
    .string()
    .min(1, 'organizations.validation.nameRequired')
    .max(255, 'organizations.validation.nameTooLong'),
  description: z.string().optional(),
  website: z.string().url('organizations.validation.websiteInvalid').optional().or(z.literal('')),
});

type UpdateOrgFormData = z.infer<typeof updateOrgSchema>;

interface GeneralTabProps {
  organization: Organization;
  onUpdate: (organization: Organization) => void;
}

function GeneralTab({ organization, onUpdate }: GeneralTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [confirmName, setConfirmName] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<UpdateOrgFormData>({
    resolver: zodResolver(updateOrgSchema),
    defaultValues: {
      name: organization.name,
      description: organization.description || '',
      website: organization.website || '',
    },
  });

  const handleSave = async (data: UpdateOrgFormData) => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const updatedOrg = await organizationService.update(organization.id, {
        name: data.name,
        // REV-956: null clears it. Omitting the key would keep the old text.
        description: data.description || null,
        website: data.website || null,
      });
      onUpdate(updatedOrg);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(toDisplayError(err, t('organizations.errors.updateFailed')));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (confirmName !== organization.name) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await organizationService.delete(organization.id);
      void navigate('/orgs');
    } catch (err) {
      setDeleteError(toDisplayError(err, t('organizations.errors.deleteFailed')));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* General Settings */}
      <Card sx={{ mb: 3 }}>
        <form onSubmit={handleSubmit(handleSave)}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('organizations.settings.general')}
            </Typography>

            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {saveError}
              </Alert>
            )}

            {saveSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('organizations.settings.saved')}
              </Alert>
            )}

            <TextField
              {...register('name')}
              label={t('organizations.fields.name')}
              fullWidth
              margin="normal"
              error={!!errors.name}
              helperText={errors.name ? t(errors.name.message as string) : undefined}
              disabled={saving}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              label={t('organizations.fields.slug')}
              fullWidth
              margin="normal"
              value={organization.slug}
              disabled
              helperText={t('organizations.fields.slugHint')}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              {...register('description')}
              label={t('organizations.fields.description')}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              error={!!errors.description}
              helperText={errors.description ? t(errors.description.message as string) : undefined}
              disabled={saving}
              slotProps={{ inputLabel: { shrink: true } }}
            />

            <TextField
              {...register('website')}
              label={t('organizations.fields.website')}
              fullWidth
              margin="normal"
              placeholder={t('organizations.fields.websitePlaceholder', 'https://example.com')}
              error={!!errors.website}
              helperText={errors.website ? t(errors.website.message as string) : undefined}
              disabled={saving}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('organizations.settings.save')
              )}
            </Button>
          </CardActions>
        </form>
      </Card>

      {/* Danger Zone */}
      <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <CardContent>
          <Typography variant="h6" color="error" gutterBottom>
            {t('organizations.settings.danger')}
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('organizations.delete.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('organizations.delete.warning')}
          </Typography>

          <Button variant="outlined" color="error" onClick={() => setDeleteDialogOpen(true)}>
            {t('organizations.delete.submit')}
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">{t('organizations.delete.title')}</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText sx={{ mb: 2 }}>{t('organizations.delete.warning')}</DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            {t('organizations.delete.confirmText')}
          </DialogContentText>
          <TextField
            fullWidth
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={organization.name}
            disabled={deleting}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting || confirmName !== organization.name}
          >
            {deleting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t('organizations.delete.submit')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GeneralTab;
